import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CacheKeyEnum, CategoryService, ConfirmationComponent, Pager, PreviewMediaComponent, Response, ResponseService, ResponseTypeEnum, UtilHelper } from 'lib-trend-core';
import { Observable, Subject, debounceTime, distinctUntilChanged, forkJoin, switchMap, takeUntil } from 'rxjs';
import { ResponseEditComponent } from './response-edit/response-edit.component';

@Component({
    selector: 'app-response-component',
    // templateUrl: 'response.component.html',
    templateUrl: 'response.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResponseComponent extends AbstractComponent implements OnInit {

  pager: Pager<Response> = new Pager<Response>({ perPage: 10 });
  listObservable: Observable<Pager<Response>>;
  private termoDaBusca: Subject<string> = new Subject<string>();

  listCategory: any[] = [];

  labelFilter: string;

  constructor(
    injector: Injector,
    public dialog: MatDialog,
    public responseService: ResponseService,
    private categoryService: CategoryService,
    private matDialogPreview: MatDialog,
  ) {
    super(injector);
    this.createForm();
    this.loadingContent = true;
    this.cacheKey = CacheKeyEnum.QUICK_RESPONSES_LIST;
  }

  ngOnInit(): void {
    this.getListCategory();
  }

  ngAfterViewInit(): void {
    const cached = this.restoreState<Response>();
    if (!cached) {
      this.getList();
      return;
    }
    this.pager = cached.pager;
    this.searchString = cached.search ?? '';
    this.searchParams = cached.params ?? {};

    this.labelFilter = this.searchString || this.t('response.home.categories');
    this.loadingContent = false;

    if (this.searchString) {
      this.formGroup.get('search').setValue(this.searchString);
    }

    this.setupObservableSearch();
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      type: [ResponseTypeEnum.QUICK],
      search: ['']
    });
  }

  setupObservableSearch() {
    this.listObservable = this.termoDaBusca.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term => {
        this.searchString = term;
        return this.responseService.getAll(this.pager.page, this.pager.perPage, term, this.searchParams);
      }),
    );
    this.listObservable.subscribe((pager: Pager<Response>) => {
      this.pager = pager;
      this.persistListState(this.pager, this.searchString, this.searchParams);
    });
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  search(termo: string) {
    this.pager.page = 1;
    this.searchString = termo;
    this.termoDaBusca.next(termo);
  }

  getList() {
    this.loadingContent = true;
    this.searchParams = {
      company: super.getIDCurrentCompany(),
      type: ResponseTypeEnum.QUICK,
    };

    this.responseService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams).subscribe({
      next: (value) => {
        this.pager = value;
        this.setupObservableSearch();
        this.persistListState(this.pager, this.searchString, this.searchParams);
      },
      complete: () => this.loadingContent = false,
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(ResponseEditComponent, {
      width: '900px',
      data: { type: this.formGroup.get('type')?.value }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.getList();
    });
  }

  edit(item: Response): void {
    const dialogRef = this.dialog.open(ResponseEditComponent, {
      width: '900px',
      data: item
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.getList();
    });
  }

  delete(item: Response): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) == true) {
        this.responseService.delete(item._id).subscribe({
          next: (value) => {
            this.alertService.success(this.t('response.home.deleteSuccess'));
            this.getList();
          },
          error: (err) => this.alertService.error(err.error.message)
        });
      };
    });
  }

  preview(item: Response): void {
    const dialogRef = this.matDialogPreview.open(PreviewMediaComponent, {
      width: '600px',
      data: {
        isTableResponseApp: true,
        response: item,
      }
    });
  }

  selectedCategory(category: string): void {
    this.searchString = category;
    this.labelFilter = category === '' ? this.t('response.home.categories') : category;
    this.getList();
  }

  private getListCategory(): void {
    this.categoryService.getList().subscribe({
      next: (value) => {
        this.listCategory = value;
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  public getFileExtension(item: Response) {
    if (item.buttonActions?.length > 0) {
      return this.t('response.type.buttons');
    }
    if (item.vcardContact) {
      return this.t('response.type.contact');
    }
    if (item.place) {
      return this.t('response.type.location');
    }
    return UtilHelper.getFileExtension(item.contentType);
  }
}
